import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Plus, PencilSimple, Trash, CheckCircle, Warning, MagnifyingGlass, Link as LinkIcon, X, User, UsersFour } from "@phosphor-icons/react";
import { ROLE_LABELS, initials } from "@/lib/helpers";
import { useAuth } from "@/contexts/AuthContext";
import { SearchBar } from "./_shared";
import { FileUpload } from "@/components/ui/file-upload";
import { FileImage } from "@/components/ui/file-image";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { PlanGatedButton } from "@/components/PlanGating";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from "@/components/ui/command";

const HARDCODED_ROLES = ["owner", "admin", "reception", "therapist", "guardian", "accountant"];
// iter-57 — Additive QR roles. Grouped separately in the picker so
// they read as "on top of your primary role" rather than as a
// standalone role. They can be combined with any non-exclusive role.
const ADDITIVE_ROLES = ["display_qr", "scan_qr"];
// Owner is set once at signup and never editable via UI (it's a workspace-
// creator role, not a permission the admin can grant). Every other role is
// freely assignable — and we fetch the full custom-role list at mount so
// admin-created roles show up in the picker too.
const COMP_MODELS = [
  { id: "salaried_monthly", label: "Salaried (monthly)" },
  { id: "monthly_contract", label: "Monthly contract" },
  { id: "per_session",      label: "Per session" },
  { id: "per_day",          label: "Per day" },
  { id: "hourly",           label: "Hourly" },
];

export default function UsersTab() {
  const { user: currentUser, refreshUser } = useAuth();
  const [items, setItems] = useState([]);
  const [branches, setBranches] = useState([]);
  const [availableRoles, setAvailableRoles] = useState(HARDCODED_ROLES);
  const [q, setQ] = useState("");
  const [show, setShow] = useState(false);
  const [editing, setEditing] = useState(null);
  // Default password suggestion for the invite dialog. Regenerated per
  // mount so we don't ship the *same* default across every install (the
  // admin still sees + can edit it before saving).
  const [defaultInvitePw] = useState(() => {
    const s = Math.random().toString(36).slice(2, 10);
    return `Tf-${s}!`;
  });
  const emptyForm = { name: "", email: "", phone: "", password: defaultInvitePw, roles: ["reception"], branch_ids: [], photo_file_id: null, app_access_enabled: true };
  const [form, setForm] = useState(emptyForm);

  const load = async () => {
    try {
      const bust = Date.now();
      const [u, b, r] = await Promise.all([
        api.get(`/users?_=${bust}`),
        api.get("/branches"),
        api.get("/roles"),
      ]);
      setItems(u.data || []);
      setBranches(b.data || []);
      // Union of built-ins + workspace-defined custom role keys, with owner
      // shown for display but disabled in the picker below.
      const keys = (r.data?.roles || []).map(x => x.key);
      const merged = Array.from(new Set([...HARDCODED_ROLES, ...keys]));
      setAvailableRoles(merged);
      await refreshUser?.();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  // Phase 4-b — Parent/Guardian is mutually exclusive with every
  // other role. Selecting one greys out the other and vice versa.
  const isExclusive = (r) => r === "guardian" || r === "parent";
  const roleDisabled = (r) => {
    const cur = form.roles || [];
    if (isExclusive(r)) return cur.some(x => !isExclusive(x));
    return cur.some(x => isExclusive(x));
  };
  const toggleRole = (r) => setForm(f => {
    const has = (f.roles || []).includes(r);
    if (has) return { ...f, roles: f.roles.filter(x => x !== r) };
    // Selecting an exclusive role removes non-exclusive ones (and vice versa)
    if (isExclusive(r)) return { ...f, roles: [r] };
    return { ...f, roles: [...(f.roles || []).filter(x => !isExclusive(x)), r] };
  });
  const toggleBranch = (id) => setForm(f => ({ ...f, branch_ids: f.branch_ids.includes(id) ? f.branch_ids.filter(x=>x!==id) : [...f.branch_ids, id] }));

  const openNew = () => { setEditing(null); setForm(emptyForm); setShow(true); };
  const openEdit = (u) => {
    setEditing(u);
    setForm({
      name: u.name || "",
      email: u.email || "",
      phone: u.phone || "",
      password: "",
      roles: u.roles || [],
      branch_ids: u.branch_ids || [],
      photo_file_id: u.photo_file_id || null,
      app_access_enabled: u.app_access_enabled !== false,
    });
    setShow(true);
  };

  const submit = async () => {
    if (!form.name) { toast.error("Name is required"); return; }
    if (form.roles.length === 0) { toast.error("Pick at least one role"); return; }
    // Phase 4-b — enforce app_access rules on the client too (backend
    // also validates). When access is enabled email + password must
    // both be present. When disabled they're optional.
    if (form.app_access_enabled) {
      if (!form.email) { toast.error("Email is required when Web Application Access is enabled"); return; }
      if (!editing && !form.password) { toast.error("Password is required when Web Application Access is enabled"); return; }
    }
    try {
      if (editing) {
        const payload = {
          name: form.name,
          phone: form.phone,
          roles: form.roles,
          branch_ids: form.branch_ids,
          photo_file_id: form.photo_file_id,
          app_access_enabled: form.app_access_enabled,
        };
        // Send email only if it actually changed — the backend will
        // reset email confirmation and dispatch a fresh confirm link.
        if (form.email && form.email.toLowerCase().trim() !== (editing.email || "").toLowerCase()) {
          payload.email = form.email.toLowerCase().trim();
        }
        await api.put(`/users/${editing.id}`, payload);
        toast.success("User updated" + (payload.email ? " — new email needs to be re-confirmed" : ""));
      } else {
        await api.post("/users", form);
        toast.success("User added");
      }
      setShow(false); setEditing(null); setForm(emptyForm);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const toggleActive = async (u) => {
    const nextActive = u.active === false;
    try {
      await api.put(`/users/${u.id}`, { active: nextActive });
      toast.success(nextActive ? "User activated" : "User deactivated");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const transferOwnership = async (target) => {
    const label = `${target.name || target.email}`;
    if (!window.confirm(
      `Transfer workspace ownership to ${label}?\n\n` +
      `• ${label} will gain the Owner role.\n` +
      `• You will lose the Owner role (your other roles stay).\n` +
      `• This cannot be undone from the UI — the new Owner would have to hand it back.`
    )) return;
    try {
      await api.post(`/users/${target.id}/transfer-ownership`, { target_user_id: target.id });
      toast.success(`Ownership transferred to ${label}`);
      // The caller no longer has Owner, refresh their session so UI gates update.
      await refreshUser?.();
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const deleteUser = async (target) => {
    const label = `${target.name || target.email}`;
    if (!window.confirm(
      `Permanently delete ${label}?\n\n` +
      `Their user record will be removed. Historical entries (sessions, invoices) they authored will remain but the user will show as "Unknown".\n\n` +
      `This cannot be undone.`
    )) return;
    try {
      await api.delete(`/users/${target.id}`);
      toast.success(`Deleted ${label}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const resendConfirmation = async (target) => {
    try {
      await api.post("/auth/resend-confirmation", { email: target.email });
      toast.success(`Confirmation email re-sent to ${target.email}`);
    } catch (e) { toast.error(formatApiError(e)); }
  };


  const isCurrentOwner = (currentUser?.roles || []).includes("owner");

  const activeOwnerCount = items.filter(u => (u.roles || []).includes("owner") && u.active !== false).length;

  const filtered = q.trim()
    ? items.filter(u => {
        const s = q.trim().toLowerCase();
        const roleLabels = (u.roles || []).map(r => (ROLE_LABELS[r] || r).toLowerCase()).join(" ");
        return (u.name || "").toLowerCase().includes(s)
          || (u.email || "").toLowerCase().includes(s)
          || (u.phone || "").toLowerCase().includes(s)
          || roleLabels.includes(s);
      })
    : items;

  return (
    <Card>
      <CardHeader className="gap-3">
        <div className="flex flex-row justify-between items-center gap-3">
          <CardTitle className="text-base font-display tracking-tight">Users ({filtered.length}{q && ` / ${items.length}`})</CardTitle>
          <PlanGatedButton flagKey="can_add_user" size="sm" onClick={openNew} className="gap-1.5" data-testid="new-user-btn"><Plus size={14} weight="bold" />New user</PlanGatedButton>
        </div>
        <SearchBar value={q} onChange={setQ} placeholder="Search by name, email, phone, or role…" testId="users-search" />
      </CardHeader>
      <CardContent className="p-0">
        <div className="divide-y divide-border">
          {filtered.length === 0 && (
            <div className="px-5 py-8 text-center text-sm text-muted-foreground">
              {q ? `No users match "${q}"` : "No users yet."}
            </div>
          )}
          {filtered.map(u => {
            const inactive = u.active === false;
            const isOwner = (u.roles || []).includes("owner");
            const isLastOwner = isOwner && activeOwnerCount <= 1 && !inactive;
            const isSelf = currentUser && currentUser.id === u.id;
            return (
              <div key={u.id} className={`px-5 py-3 flex items-center gap-3 ${inactive ? "opacity-60" : ""}`} data-testid={`user-row-${u.id}`}>
                <Avatar className="h-8 w-8 shrink-0">
                  {u.photo_file_id ? (
                    <FileImage
                      fileId={u.photo_file_id}
                      alt=""
                      className="aspect-square h-full w-full object-cover"
                      fallback={
                        <AvatarFallback className="bg-primary/15 text-primary font-semibold text-xs">
                          {initials(u.name || u.email)}
                        </AvatarFallback>
                      }
                    />
                  ) : (
                    <AvatarFallback className="bg-primary/15 text-primary font-semibold text-xs">
                      {initials(u.name || u.email)}
                    </AvatarFallback>
                  )}
                </Avatar>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate flex items-center gap-1">
                    {u.name}
                    {isSelf && <Badge variant="outline" className="ml-1 text-[10px]">You</Badge>}
                    {inactive && <Badge className="ml-1 text-[10px] bg-muted text-muted-foreground border-border">Inactive</Badge>}
                  </div>
                  <div className="text-xs text-muted-foreground truncate flex items-center gap-1.5" title={u.email_confirmed_at ? `Email confirmed ${u.email_confirmed_at}` : "Email not confirmed"}>
                    {u.email_confirmed_at ? (
                      <CheckCircle size={13} weight="fill" className="text-emerald-600 shrink-0" data-testid={`email-confirmed-${u.id}`} />
                    ) : (
                      <Warning size={13} weight="fill" className="text-amber-600 shrink-0" data-testid={`email-unconfirmed-${u.id}`} />
                    )}
                    <span className="truncate">{u.email}{u.phone && ` · ${u.phone}`}</span>
                  </div>
                </div>
                <div className="flex gap-1 flex-wrap max-w-[180px] justify-end">
                  {(u.roles || []).slice(0, 3).map(r => (
                    <Badge key={r} variant="outline" className="text-[10px]">{ROLE_LABELS[r] || r}</Badge>
                  ))}
                  {(u.roles || []).length > 3 && <Badge variant="outline" className="text-[10px]">+{u.roles.length - 3}</Badge>}
                </div>
                <Button size="sm" variant="ghost" onClick={() => openEdit(u)} data-testid={`edit-user-${u.id}`} className="gap-1.5"><PencilSimple size={14} /> Edit</Button>
                {!u.email_confirmed_at && !inactive && (
                  <Button
                    size="sm" variant="ghost"
                    onClick={() => resendConfirmation(u)}
                    data-testid={`resend-conf-${u.id}`}
                    title="Resend the email confirmation link"
                    className="text-xs text-emerald-700 hover:bg-emerald-50"
                  >
                    Resend confirm
                  </Button>
                )}
                {isCurrentOwner && !isSelf && !isOwner && !inactive && (
                  <Button
                    size="sm" variant="ghost"
                    onClick={() => transferOwnership(u)}
                    data-testid={`transfer-owner-${u.id}`}
                    title="Hand the workspace-Owner role to this user"
                    className="text-xs text-amber-700 hover:bg-amber-50"
                  >
                    Make owner
                  </Button>
                )}
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => toggleActive(u)}
                  disabled={isLastOwner}
                  title={isLastOwner ? "Cannot deactivate the last active owner" : ""}
                  data-testid={`toggle-user-${u.id}`}
                  className="text-xs"
                >
                  {inactive ? "Activate" : "Deactivate"}
                </Button>
                {isCurrentOwner && !isSelf && (
                  <Button
                    size="sm" variant="ghost"
                    onClick={() => deleteUser(u)}
                    disabled={isLastOwner}
                    title={isLastOwner ? "Cannot delete the last owner" : "Delete this user permanently"}
                    data-testid={`delete-user-${u.id}`}
                    className="text-xs text-red-700 hover:bg-red-50 gap-1"
                  >
                    <Trash size={13} />
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      </CardContent>

      <Dialog open={show} onOpenChange={(o) => { setShow(o); if (!o) { setEditing(null); } }}>
        <DialogContent className="bg-card max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display tracking-tight">{editing ? `Edit user · ${editing.name}` : "New user"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {!editing && (
              <LinkExistingPersonPicker
                onPick={(p) => {
                  // Auto-fill the invite form from the picked employee/guardian.
                  // Roles + branch are only *suggested*; admin can still override.
                  setForm(f => ({
                    ...f,
                    name: p.name || f.name,
                    email: p.email || f.email,
                    phone: p.phone || f.phone,
                    roles: p.suggested_role && !f.roles.includes(p.suggested_role) ? [p.suggested_role] : f.roles,
                    branch_ids: p.branch_id && !f.branch_ids.includes(p.branch_id) ? [p.branch_id] : f.branch_ids,
                    _linked_source: { kind: p.kind, source_id: p.source_id, guardian_index: p.guardian_index },
                  }));
                }}
                linked={form._linked_source}
                onClear={() => setForm(f => { const { _linked_source, ...rest } = f; return rest; })}
              />
            )}
            {editing && (
              <div className="pb-3 border-b border-border">
                <Label className="text-[10px] uppercase tracking-wide text-muted-foreground">Profile photo</Label>
                <div className="mt-2">
                  <FileUpload
                    value={form.photo_file_id}
                    onChange={(id) => setForm(f => ({ ...f, photo_file_id: id }))}
                    entityType="user"
                    entityId={editing.id}
                    accept="image/jpeg,image/png,image/webp"
                    variant="avatar"
                    size={80}
                  />
                </div>
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Name</Label><Input value={form.name} onChange={e=>setForm({...form, name:e.target.value})} data-testid="user-name" /></div>
              <div className="space-y-1.5"><Label>Email</Label><Input type="email" value={form.email} onChange={e=>setForm({...form, email:e.target.value})} data-testid="user-email" /></div>
              <div className="space-y-1.5"><Label>Phone</Label><Input value={form.phone} onChange={e=>setForm({...form, phone:e.target.value})} /></div>
              {!editing && (
                <div className="space-y-1.5"><Label>Password</Label><Input value={form.password} onChange={e=>setForm({...form, password:e.target.value})} /></div>
              )}
            </div>
            {editing && <div className="text-[11px] text-muted-foreground">Password can only be set from the login &ldquo;Forgot password&rdquo; flow.</div>}
            <div className="space-y-1.5">
              <Label>Roles</Label>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {/* Owner is workspace-creator-only: readonly badge, never editable */}
                {editing && (editing.roles || []).includes("owner") && (
                  <div className="flex items-center gap-2 p-2 border rounded text-sm border-primary/40 bg-primary/5 opacity-90 cursor-not-allowed" data-testid="role-owner-locked" title="Owner is assigned at signup and cannot be changed.">
                    <Checkbox checked disabled />
                    <span>{ROLE_LABELS.owner || "Owner"}</span>
                    <Badge variant="outline" className="ml-auto text-[10px]">Locked</Badge>
                  </div>
                )}
                {availableRoles.filter(r => r !== "owner" && !ADDITIVE_ROLES.includes(r)).map(r => {
                  const disabled = roleDisabled(r);
                  const checked  = form.roles.includes(r);
                  const tooltip  = disabled
                    ? "Parent/Guardian role cannot be combined with any other role."
                    : undefined;
                  return (
                    <label
                      key={r}
                      title={tooltip}
                      className={`flex items-center gap-2 p-2 border rounded text-sm ${
                        checked ? "border-primary bg-primary/5" : "border-border"
                      } ${disabled ? "opacity-40 cursor-not-allowed" : "cursor-pointer"}`}
                      data-testid={`role-checkbox-${r}`}
                    >
                      <Checkbox checked={checked} disabled={disabled} onCheckedChange={() => !disabled && toggleRole(r)} />
                      {ROLE_LABELS[r] || r}
                    </label>
                  );
                })}
              </div>
              <p className="text-[10px] text-muted-foreground">Owner is the workspace creator; set at signup and locked here. To transfer ownership, contact your platform admin.</p>

              {/* iter-57 — Additive QR roles picker. Rendered as a
                  separate group so admins understand these stack on
                  top of the primary role and gate the top-bar buttons. */}
              <div className="pt-3 mt-1 border-t border-border">
                <Label className="text-xs">QR access <span className="text-muted-foreground font-normal">(additive)</span></Label>
                <p className="text-[10px] text-muted-foreground mb-2">
                  Adds a Display QR / Scan QR button in the top bar. Can be granted alongside any primary role — e.g. today&apos;s receptionist gets Display QR temporarily.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {ADDITIVE_ROLES.map(r => {
                    const guardianOnly = (form.roles || []).some(x => x === "guardian" || x === "parent");
                    const checked = form.roles.includes(r);
                    return (
                      <label
                        key={r}
                        title={guardianOnly ? "Parent / Guardian accounts cannot hold QR roles." : undefined}
                        className={`flex items-center gap-2 p-2 border rounded text-sm ${
                          checked ? "border-primary bg-primary/5" : "border-border"
                        } ${guardianOnly ? "opacity-40 cursor-not-allowed" : "cursor-pointer"}`}
                        data-testid={`role-checkbox-${r}`}
                      >
                        <Checkbox
                          checked={checked}
                          disabled={guardianOnly}
                          onCheckedChange={() => !guardianOnly && toggleRole(r)}
                        />
                        {ROLE_LABELS[r] || r}
                      </label>
                    );
                  })}
                </div>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Branches</Label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {branches.map(b => (
                  <label key={b.id} className={`flex items-center gap-2 p-2 border rounded cursor-pointer text-sm ${form.branch_ids.includes(b.id) ? "border-primary bg-primary/5" : "border-border"}`}>
                    <Checkbox checked={form.branch_ids.includes(b.id)} onCheckedChange={() => toggleBranch(b.id)} />
                    {b.name}
                  </label>
                ))}
              </div>
            </div>

            {/* --- Phase 4-b: Enable Web Application Access ------- */}
            <div className="pt-3 border-t border-border space-y-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <Checkbox
                  checked={!!form.app_access_enabled}
                  onCheckedChange={v => setForm(f => ({ ...f, app_access_enabled: !!v }))}
                  data-testid="user-app-access"
                />
                <span className="text-sm font-medium">Enable Web Application Access</span>
              </label>
              <div className="text-[11px] text-muted-foreground pl-6">
                {form.app_access_enabled
                  ? "Email and password above are required. This user can sign in with these credentials."
                  : "Login is disabled — email and password are optional. Toggle this on later to activate access."}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShow(false)}>Cancel</Button>
            <Button onClick={submit} data-testid="save-user">{editing ? "Save changes" : "Save"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}


/**
 * LinkExistingPersonPicker — searchable dropdown of Active employees +
 * student guardians who don't yet have an app login. Prevents duplicate
 * identities when an admin adds a user for someone already in the
 * Employee Master / Student roster.
 *
 * Data source: ``GET /api/users/linkable?q=…`` — server does the filtering
 * (Clerks excluded, already-linked hidden, existing Parent/Guardian
 * users blocked).
 */
function LinkExistingPersonPicker({ onPick, linked, onClear }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    let cancel = false;
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/users/linkable", { params: q ? { q } : {} });
        if (!cancel) setItems(data?.items || []);
      } catch (e) {
        if (!cancel) toast.error(formatApiError(e));
      } finally { if (!cancel) setLoading(false); }
    }, 200);   // debounce
    return () => { cancel = true; clearTimeout(t); };
  }, [q, open]);

  if (linked) {
    return (
      <div
        className="flex items-center gap-2 rounded-md border border-primary/30 bg-primary/5 px-3 py-2 text-sm"
        data-testid="user-linked-source"
      >
        <LinkIcon size={14} weight="duotone" className="text-primary" />
        <span className="font-medium">
          Linking to existing {linked.kind === "employee" ? "Employee" : "Guardian"} record
        </span>
        <button
          type="button"
          onClick={onClear}
          className="ml-auto text-muted-foreground hover:text-foreground p-1"
          aria-label="Unlink"
          data-testid="user-linked-clear"
        >
          <X size={14} />
        </button>
      </div>
    );
  }

  return (
    <div className="rounded-md border border-dashed border-border p-3 space-y-2" data-testid="link-existing-block">
      <div className="flex items-center gap-2">
        <LinkIcon size={14} weight="duotone" className="text-muted-foreground" />
        <span className="text-sm font-medium">Link to an existing person</span>
        <Badge variant="outline" className="text-[10px]">Optional</Badge>
      </div>
      <p className="text-[11px] text-muted-foreground">
        Avoid duplicates — pick an Active employee or student guardian who does not yet have app access.
        Clerks are excluded and never get logins.
      </p>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button" variant="outline"
            className="w-full justify-start gap-2 h-9"
            data-testid="link-existing-trigger"
          >
            <MagnifyingGlass size={14} className="text-muted-foreground" />
            <span className="text-muted-foreground text-xs">Search employees & guardians…</span>
          </Button>
        </PopoverTrigger>
        <PopoverContent className="p-0 w-[--radix-popover-trigger-width]" align="start">
          <Command shouldFilter={false}>
            <CommandInput
              placeholder="Type a name, email, or role…"
              value={q}
              onValueChange={setQ}
              data-testid="link-existing-search"
            />
            <CommandList>
              {loading && <div className="p-3 text-xs text-muted-foreground text-center">Loading…</div>}
              {!loading && items.length === 0 && (
                <CommandEmpty>
                  <div className="text-xs text-muted-foreground py-2">
                    {q ? `No linkable person matches "${q}".` : "Everyone with app access is already linked."}
                  </div>
                </CommandEmpty>
              )}
              {items.length > 0 && (
                <CommandGroup heading={`${items.length} person${items.length === 1 ? "" : "s"} available`}>
                  {items.map((p, i) => {
                    const Icon = p.kind === "employee" ? User : UsersFour;
                    const dedupeKey = `${p.kind}:${p.source_id}:${p.guardian_index ?? ""}:${i}`;
                    return (
                      <CommandItem
                        key={dedupeKey}
                        value={dedupeKey}   // ensure unique cmdk value
                        onSelect={() => { onPick(p); setOpen(false); setQ(""); }}
                        className="gap-2"
                        data-testid={`link-existing-option-${p.kind}-${p.source_id}`}
                      >
                        <Icon size={14} weight="duotone" className={p.kind === "employee" ? "text-emerald-700" : "text-sky-700"} />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium truncate">{p.name}</div>
                          <div className="text-[10px] text-muted-foreground truncate">
                            {p.hint}{p.email && ` · ${p.email}`}{p.phone && !p.email && ` · ${p.phone}`}
                          </div>
                        </div>
                        <Badge className="text-[9px] bg-muted text-muted-foreground border-border">{p.kind}</Badge>
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}
